import { clsx } from 'clsx';
import { useState } from 'react';
import type { TierId } from '#shared/core/ratings';
import { tierFill } from '@/lib/rating';
import { PASTELS, TONE_BG, type Tone } from './tones';

/**
 * Square-ish sticker avatar: portrait image when there is one, initials on a pastel otherwise
 * (also when the image fails). Greek names work: initials take the first letter of the first
 * two words. `ring` draws a tier-colored frame outside the ink border.
 *
 *   <Avatar name="Giorgos Papadakis" src={thumbUrl} size={40} />
 *   <Avatar name="Νίκος" tone="peach" ring="franchise" />
 */
export type AvatarSize = 24 | 32 | 40 | 56 | 72 | 96;

const BOX: Record<AvatarSize, string> = {
  24: 'size-6 text-[10px] border-2',
  32: 'size-8 text-xs border-2',
  40: 'size-10 text-sm border-(length:--tts-border)',
  56: 'size-14 text-lg border-(length:--tts-border)',
  72: 'size-18 text-2xl border-(length:--tts-border-heavy)',
  96: 'size-24 text-3xl border-(length:--tts-border-heavy)',
};

export interface AvatarProps {
  name: string;
  src?: string | null;
  size?: AvatarSize;
  /** Fill behind the initials. Default: picked from the name so it stays stable. */
  tone?: Tone;
  ring?: TierId | null;
  /** Decorative when the name is printed next to it. */
  decorative?: boolean;
  className?: string;
}

/** "Giorgos Papadakis" → "GP", "νίκος" → "Ν", "" → "?". */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const first = (w: string) => [...w][0] ?? '';
  const out = words.length === 1 ? first(words[0] ?? '') : first(words[0] ?? '') + first(words[words.length - 1] ?? '');
  return out.toLocaleUpperCase();
}

/** Stable pastel for a name (same name, same color on every phone). */
export function toneFor(name: string): Tone {
  let h = 0;
  for (const ch of name) h = (h * 31 + (ch.codePointAt(0) ?? 0)) >>> 0;
  return PASTELS[h % PASTELS.length] ?? 'lavender';
}

export function Avatar({ name, src, size = 40, tone, ring, decorative = false, className }: AvatarProps) {
  // Remember which src failed, so a new src gets a fresh try without an effect.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showImg = Boolean(src) && failedSrc !== src;
  const a11y = decorative ? { 'aria-hidden': true as const } : { role: 'img' as const, 'aria-label': name };
  return (
    <span
      {...a11y}
      className={clsx(
        'relative inline-grid shrink-0 place-items-center overflow-hidden rounded-tts border-ink font-display font-black italic leading-none',
        BOX[size],
        !showImg && TONE_BG[tone ?? toneFor(name)],
        className,
      )}
      style={ring ? { boxShadow: `0 0 0 3px ${tierFill(ring)}, 0 0 0 5px #000` } : undefined}
    >
      {showImg ? (
        <img
          src={src ?? undefined}
          alt=""
          className="size-full object-cover"
          loading="lazy"
          decoding="async"
          onError={() => setFailedSrc(src ?? null)}
        />
      ) : (
        <span aria-hidden="true">{initials(name)}</span>
      )}
    </span>
  );
}
